// Backup history management utilities
// Stores backup metadata in localStorage

const HISTORY_KEY = 'canokey_backup_history';
const MAX_HISTORY_ITEMS = 20;

/**
 * Get backup history from localStorage
 * @returns {Array} Array of backup history items
 */
export function getBackupHistory() {
  try {
    const history = localStorage.getItem(HISTORY_KEY);
    return history ? JSON.parse(history) : [];
  } catch (error) {
    console.error('Failed to load backup history:', error);
    return [];
  }
}

/**
 * Add a backup record to history
 * @param {Object} backupInfo - Backup metadata
 * @returns {boolean} Success status
 */
export function addBackupToHistory(backupInfo) {
  try {
    const history = getBackupHistory();

    const record = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      fileName: backupInfo.fileName,
      encrypted: backupInfo.encrypted || false,
      device: backupInfo.device || {},
      includedData: {
        piv: backupInfo.includePIV || false,
        openpgp: backupInfo.includeOpenPGP || false,
        oath: backupInfo.includeOATH || false
      },
      size: backupInfo.size || 0,
      success: true
    };

    // Add to beginning of array
    history.unshift(record);

    // Keep only the most recent items
    const trimmedHistory = history.slice(0, MAX_HISTORY_ITEMS);

    localStorage.setItem(HISTORY_KEY, JSON.stringify(trimmedHistory));
    return true;
  } catch (error) {
    console.error('Failed to save backup to history:', error);
    return false;
  }
}

/**
 * Remove a backup record from history
 * @param {string} id - Backup record ID
 * @returns {boolean} Success status
 */
export function removeBackupFromHistory(id) {
  try {
    const history = getBackupHistory();
    const filtered = history.filter(item => item.id !== id);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(filtered));
    return true;
  } catch (error) {
    console.error('Failed to remove backup from history:', error);
    return false;
  }
}

/**
 * Clear all backup history
 * @returns {boolean} Success status
 */
export function clearBackupHistory() {
  try {
    localStorage.removeItem(HISTORY_KEY);
    return true;
  } catch (error) {
    console.error('Failed to clear backup history:', error);
    return false;
  }
}

/**
 * Get backup statistics
 * @returns {Object} Statistics object
 */
export function getBackupStatistics() {
  const history = getBackupHistory();

  return {
    totalBackups: history.length,
    encryptedBackups: history.filter(b => b.encrypted).length,
    lastBackupDate: history.length > 0 ? history[0].timestamp : null,
    totalSize: history.reduce((sum, b) => sum + (b.size || 0), 0)
  };
}
